import { Trans, useLingui } from "@lingui/react/macro";
import { providerFullDisplayName } from "@pontimed/types";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { BeamFrame } from "@pontimed/ui/puls";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  CalendarCheck,
  CalendarClock,
  CalendarX2,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  RotateCw,
  Stethoscope,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";

import { Button, buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiErrorMessage } from "@/lib/api-error-message";

const SUPPORT_EMAIL = "support@pontimed.app";

interface ConfirmationDetails {
  appointment: {
    id: string;
    startsAt: string;
    endsAt: string;
    type: string;
    status: string;
    confirmedAt: string | null;
    confirmationChannel: string | null;
    provider: {
      firstName: string;
      lastName: string;
      profession?: string | null;
      photoUrl?: string | null;
    } | null;
    facility: {
      name: Record<string, string> | string | null;
      addressFormatted?: string | null;
      addressCity?: string | null;
    } | null;
  };
  expired: boolean;
  alreadyConfirmed: boolean;
  /** Moved by the doctor or the clinic and not answered yet. */
  awaitingPatientResponse?: boolean;
}

type ActionStatus = "idle" | "submitting" | "success" | "error";

/**
 * Public landing page for No-Show Shield confirmation links.
 *
 * Sits at root (not under `_public` or `_authenticated`) so email-link
 * landings work whether the recipient is logged in or anonymous — the
 * token IS the authentication.
 *
 * After the doctor or the clinic moved the appointment
 * (`awaitingPatientResponse`), the patient answers here: keep the new time
 * (= confirm), pick another time (the reschedule sheet in their appointments,
 * behind sign-in) or cancel — no late-cancellation fee, a paid consultation
 * is refunded in full.
 */
export const Route = createFileRoute("/appointments/confirm/$token")({
  component: ConfirmAppointmentPage,
});

function ConfirmAppointmentPage() {
  const { token } = Route.useParams();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;
  const errorMessage = useApiErrorMessage();

  const [details, setDetails] = useState<ConfirmationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  // P16: a bad link and a failed load are different answers — never a raw
  // "HTTP 500" or the API's English error string.
  const [loadError, setLoadError] = useState<"not_found" | "failed" | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [confirmStatus, setConfirmStatus] = useState<ActionStatus>("idle");
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [cancelStatus, setCancelStatus] = useState<ActionStatus>("idle");
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [refunded, setRefunded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    apiFetch
      .get<ConfirmationDetails>(`/api/appointments/confirm/${token}`, {
        signal: controller.signal,
        skipAuthRefresh: true,
      })
      .then((data) => setDetails(data))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLoadError(err instanceof ApiError && err.status === 404 ? "not_found" : "failed");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, reloadKey]);

  const handleConfirm = async () => {
    setConfirmStatus("submitting");
    setConfirmError(null);
    try {
      await apiFetch.post(
        `/api/appointments/confirm/${token}`,
        { channel: "web" },
        { skipAuthRefresh: true },
      );
      setConfirmStatus("success");
    } catch (err: unknown) {
      // 410 / 409 are final (the link expired or the appointment was
      // cancelled since the page loaded) — "try again" would not help.
      setConfirmError(
        err instanceof ApiError && err.status === 410
          ? t`This link has expired. Open the latest reminder or contact the clinic.`
          : err instanceof ApiError && err.status === 409
            ? t`This appointment has been cancelled.`
            : errorMessage(err, t`We couldn't confirm the appointment. Try again.`),
      );
      setConfirmStatus("error");
    }
  };

  const handleCancel = async () => {
    setCancelStatus("submitting");
    setCancelError(null);
    try {
      const res = await apiFetch.post<{ refund: { amount: number } | null }>(
        `/api/appointments/confirm/${token}/cancel`,
        {},
        { skipAuthRefresh: true },
      );
      setRefunded(!!res.refund && res.refund.amount > 0);
      setCancelStatus("success");
    } catch (err: unknown) {
      setCancelError(
        err instanceof ApiError && err.status === 410
          ? t`This link has expired. Open the latest reminder or contact the clinic.`
          : err instanceof ApiError && err.status === 409
            ? t`This appointment can no longer be cancelled from this link. See your appointments or contact the clinic.`
            : errorMessage(err, t`We couldn't cancel the appointment. Try again.`),
      );
      setCancelStatus("error");
    }
  };

  const awaiting = !!details?.awaitingPatientResponse;

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div className={STATUS_CARD} style={STATUS_CARD_STYLE}>
        {!loading && loadError ? (
          <LoadErrorBody kind={loadError} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : (
          <div className="flex flex-col items-center gap-4 text-center">
            <StatusTile icon={awaiting ? CalendarClock : CalendarCheck} tone="primary" />
            <h1 className={STATUS_TITLE} style={STAGGER_1}>
              {awaiting ? (
                <Trans>Your appointment was moved</Trans>
              ) : (
                <Trans>Confirm your appointment</Trans>
              )}
            </h1>
          </div>
        )}

        {loading && <ConfirmSkeleton />}

        {!loading && !loadError && details && (
          <ConfirmBody
            details={details}
            locale={locale}
            confirmStatus={confirmStatus}
            confirmError={confirmError}
            onConfirm={handleConfirm}
            cancelStatus={cancelStatus}
            cancelError={cancelError}
            refunded={refunded}
            onCancel={handleCancel}
          />
        )}
      </div>
    </main>
  );
}

/** Error state with its own heading and a next step (P16): a bad link says
 *  so and points to the appointments list and support; a failed load
 *  offers Retry. */
function LoadErrorBody({ kind, onRetry }: { kind: "not_found" | "failed"; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 text-center" role="alert">
      <StatusTile icon={AlertCircle} tone="destructive" />
      <h1 className={STATUS_TITLE} style={STAGGER_1}>
        {kind === "not_found" ? <Trans>Link not found</Trans> : <Trans>Something went wrong</Trans>}
      </h1>
      <p
        className="puls-reveal text-muted-foreground m-0 max-w-sm text-sm leading-relaxed"
        style={STAGGER_2}
      >
        {kind === "not_found" ? (
          <Trans>
            The link may be incomplete or expired. Open the latest reminder we sent you, or see your
            appointments.
          </Trans>
        ) : (
          <Trans>We couldn't load this appointment. Try again in a moment.</Trans>
        )}
      </p>
      <div className="flex w-full flex-col gap-2">
        {kind === "failed" && (
          <Button type="button" onClick={onRetry} className={BTN_PRIMARY}>
            <RotateCw className="size-4" aria-hidden />
            <Trans>Try again</Trans>
          </Button>
        )}
        <Link
          to="/account/user/appointments"
          search={{ tab: "upcoming" }}
          className={cn(
            buttonVariants({ variant: kind === "failed" ? "outline" : "default" }),
            kind === "failed" ? BTN_SECONDARY : BTN_PRIMARY,
          )}
        >
          <Trans>My appointments</Trans>
        </Link>
      </div>
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="text-primary inline-flex min-h-11 items-center font-mono text-sm font-medium underline-offset-4 hover:underline"
      >
        {SUPPORT_EMAIL}
      </a>
    </div>
  );
}

function ConfirmSkeleton() {
  return (
    <div className="mt-6 space-y-3">
      <Skeleton className="h-28 w-full rounded-xl" />
      <Skeleton className="h-12 w-full rounded-lg" />
    </div>
  );
}

function ConfirmBody({
  details,
  locale,
  confirmStatus,
  confirmError,
  onConfirm,
  cancelStatus,
  cancelError,
  refunded,
  onCancel,
}: {
  details: ConfirmationDetails;
  locale: string;
  confirmStatus: ActionStatus;
  confirmError: string | null;
  onConfirm: () => void;
  cancelStatus: ActionStatus;
  cancelError: string | null;
  refunded: boolean;
  onCancel: () => void;
}) {
  const { appointment, expired, alreadyConfirmed } = details;
  const awaiting = !!details.awaitingPatientResponse;
  const startsAt = new Date(appointment.startsAt);
  const dateLabel = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(startsAt);
  const timeLabel = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(startsAt);

  const providerName = appointment.provider
    ? providerFullDisplayName(appointment.provider, locale)
    : "—";

  const facilityName = pickLocalized(appointment.facility?.name ?? null, locale);
  const facilityAddress =
    appointment.facility?.addressFormatted ?? appointment.facility?.addressCity ?? null;

  const success = confirmStatus === "success";
  const cancelled =
    cancelStatus === "success" ||
    appointment.status === "cancelled" ||
    appointment.status === "declined";

  return (
    <>
      {/* Appointment ticket — same rows as the booking summary. */}
      <div className="border-border bg-background/60 mt-6 space-y-3 rounded-xl border p-4">
        <div className="flex items-center gap-3">
          <span className="bg-primary-soft text-primary grid size-9 shrink-0 place-items-center rounded-lg">
            <Stethoscope className="size-4" aria-hidden />
          </span>
          <div className="text-foreground min-w-0 text-sm font-semibold">{providerName}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="bg-muted text-muted-foreground grid size-9 shrink-0 place-items-center rounded-lg">
            <Clock className="size-4" aria-hidden />
          </span>
          <div className="text-foreground min-w-0 text-sm capitalize">
            {dateLabel}, <span className="font-mono font-semibold tabular-nums">{timeLabel}</span>
          </div>
        </div>
        {facilityName && (
          <div className="border-border flex items-center gap-3 border-t border-dashed pt-3">
            <span className="bg-muted text-muted-foreground grid size-9 shrink-0 place-items-center rounded-lg">
              <MapPin className="size-4" aria-hidden />
            </span>
            <div className="text-muted-foreground min-w-0 text-sm">
              {facilityName}
              {facilityAddress ? ` · ${facilityAddress}` : ""}
            </div>
          </div>
        )}
      </div>

      {cancelled ? (
        <div className="mt-6 flex flex-col items-center gap-3 text-center">
          <StatusTile icon={CalendarX2} tone="destructive" />
          <p
            className="puls-reveal font-display text-foreground m-0 text-lg leading-tight font-semibold tracking-[-0.015em]"
            style={STAGGER_1}
          >
            <Trans>Appointment cancelled</Trans>
          </p>
          {cancelStatus === "success" && (
            <p className="puls-reveal text-muted-foreground m-0 text-sm" style={STAGGER_2}>
              {refunded ? (
                <Trans>The clinic has been notified. Your payment will be refunded in full.</Trans>
              ) : (
                <Trans>The clinic has been notified.</Trans>
              )}
            </p>
          )}
          <Link
            to="/"
            className={cn(buttonVariants({ variant: "outline" }), "mt-2", BTN_SECONDARY)}
          >
            <Trans>Open Pontimed</Trans>
          </Link>
        </div>
      ) : expired ? (
        <p className="bg-muted text-muted-foreground mt-6 rounded-xl px-4 py-3 text-center text-sm leading-relaxed">
          <Trans>This link has expired. Open the latest reminder or contact the clinic.</Trans>
        </p>
      ) : awaiting && !success ? (
        <MovedChoices
          appointmentId={appointment.id}
          confirmStatus={confirmStatus}
          confirmError={confirmError}
          onConfirm={onConfirm}
          cancelStatus={cancelStatus}
          cancelError={cancelError}
          onCancel={onCancel}
        />
      ) : alreadyConfirmed || success ? (
        <div className="mt-6 flex flex-col items-center gap-3 text-center">
          <StatusTile icon={CheckCircle2} tone="success" />
          <p
            className="puls-reveal font-display text-foreground m-0 text-lg leading-tight font-semibold tracking-[-0.015em]"
            style={STAGGER_1}
          >
            {success && awaiting ? (
              <Trans>New time kept</Trans>
            ) : success ? (
              <Trans>Appointment confirmed</Trans>
            ) : (
              <Trans>Already confirmed</Trans>
            )}
          </p>
          <p className="puls-reveal text-muted-foreground m-0 text-sm" style={STAGGER_2}>
            <Trans>The clinic has been notified.</Trans>
          </p>
          <Link
            to="/"
            className={cn(buttonVariants({ variant: "outline" }), "mt-2", BTN_SECONDARY)}
          >
            <Trans>Open Pontimed</Trans>
          </Link>
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center gap-3">
          <BeamFrame className="w-full rounded-lg" busy={confirmStatus === "submitting"}>
            <Button
              type="button"
              onClick={onConfirm}
              disabled={confirmStatus === "submitting"}
              className={cn(BTN_PRIMARY, "sm:text-base")}
            >
              {confirmStatus === "submitting" ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trans>Confirm</Trans>
              )}
            </Button>
          </BeamFrame>
          {confirmError && (
            <p
              className="border-destructive-border bg-destructive-soft text-destructive-strong w-full rounded-lg border px-3 py-2 text-center text-sm"
              role="alert"
            >
              {confirmError}
            </p>
          )}
        </div>
      )}
    </>
  );
}

/** The patient's answer to a move by the doctor or the clinic: keep the new
 *  time, pick another one, or cancel (two taps — the second confirms). */
function MovedChoices({
  appointmentId,
  confirmStatus,
  confirmError,
  onConfirm,
  cancelStatus,
  cancelError,
  onCancel,
}: {
  appointmentId: string;
  confirmStatus: ActionStatus;
  confirmError: string | null;
  onConfirm: () => void;
  cancelStatus: ActionStatus;
  cancelError: string | null;
  onCancel: () => void;
}) {
  const [askCancel, setAskCancel] = useState(false);
  const busy = confirmStatus === "submitting" || cancelStatus === "submitting";
  const error = confirmError ?? cancelError;

  return (
    <div className="mt-6 flex flex-col items-center gap-3">
      <p className="text-muted-foreground m-0 text-center text-sm leading-relaxed">
        <Trans>
          The clinic moved your appointment to the time above. Keep it, pick another time, or cancel
          — there is no cancellation fee.
        </Trans>
      </p>

      {askCancel ? (
        <div
          className="border-destructive-border bg-destructive-soft flex w-full flex-col gap-2 rounded-xl border p-3"
          role="group"
          aria-labelledby="cancel-question"
        >
          <p id="cancel-question" className="text-foreground m-0 text-center text-sm font-semibold">
            <Trans>Cancel this appointment? The clinic will be notified.</Trans>
          </p>
          <Button
            type="button"
            variant="destructive"
            onClick={onCancel}
            disabled={busy}
            className={BTN}
          >
            {cancelStatus === "submitting" ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Trans>Yes, cancel</Trans>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setAskCancel(false)}
            disabled={busy}
            className={BTN_SECONDARY}
          >
            <Trans>Back</Trans>
          </Button>
        </div>
      ) : (
        <>
          <BeamFrame className="w-full rounded-lg" busy={confirmStatus === "submitting"}>
            <Button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={cn(BTN_PRIMARY, "sm:text-base")}
            >
              {confirmStatus === "submitting" ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trans>Keep the new time</Trans>
              )}
            </Button>
          </BeamFrame>
          <Link
            to="/account/user/appointments"
            search={{ tab: "upcoming", reschedule: appointmentId }}
            className={cn(buttonVariants({ variant: "outline" }), BTN_SECONDARY)}
          >
            <Trans>Pick another time</Trans>
          </Link>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setAskCancel(true)}
            disabled={busy}
            className={cn(BTN, "text-destructive hover:bg-destructive-soft hover:text-destructive")}
          >
            <Trans>Cancel appointment</Trans>
          </Button>
        </>
      )}

      {error && (
        <p
          className="border-destructive-border bg-destructive-soft text-destructive-strong w-full rounded-lg border px-3 py-2 text-center text-sm"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}

/* ─── Puls status screen ───
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + soft glow behind one
   centred card, state icon in a toned tile, display heading, 48px buttons.
   Presentational only. */

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 60% at 50% 40%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const STATUS_CARD_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 45%)",
};
const STATUS_CARD =
  "border-border bg-card w-full max-w-md rounded-2xl border p-6 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:p-8";
const STATUS_TITLE =
  "puls-reveal font-display text-foreground m-0 text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance";
/** `.puls-reveal` stagger steps (80 ms each, see puls.css). */
const STAGGER_1 = { "--puls-i": 1 } as CSSProperties;
const STAGGER_2 = { "--puls-i": 2 } as CSSProperties;
const BTN = "h-12 w-full gap-2 rounded-lg px-6 text-sm font-semibold";
const BTN_PRIMARY = `${BTN} hover:bg-primary-hover`;
const BTN_SECONDARY = `${BTN} bg-card text-foreground hover:border-primary/40 hover:bg-card hover:text-primary`;

type StatusTone = "primary" | "success" | "destructive";

const TILE_TONE: Record<StatusTone, string> = {
  primary: "border-primary/20 bg-primary-soft text-primary",
  success: "border-success-border bg-success-soft text-success",
  destructive: "border-destructive-border bg-destructive-soft text-destructive",
};

function StatusBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[10%] left-1/2 h-[70%] w-[min(92%,44rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}

function StatusTile({ icon: Icon, tone }: { icon: LucideIcon; tone: StatusTone }) {
  return (
    <span
      aria-hidden="true"
      className={`puls-reveal grid size-14 shrink-0 place-items-center rounded-2xl border ${TILE_TONE[tone]}`}
    >
      <Icon className="size-6" />
    </span>
  );
}

function pickLocalized(
  value: Record<string, string> | string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as Record<string, string>;
      return parsed[locale] ?? parsed.ro ?? parsed.en ?? null;
    } catch {
      return value;
    }
  }
  return value[locale] ?? value.ro ?? value.en ?? null;
}
